import { ThemeOptions } from '@mui/material';
import { fontsString } from '@repperjs/data/src/styles/fonts.styles.js';
import { THEME_BREAKPOINT } from '@repperjs/shared/src/theme.js';
import { error, grey, primary, secondary, warning } from './utils/colors.ts';
import { defaultBorderRadius, defaultButtonBorderWidth, shadowFactor, spacing } from './utils/constants.ts';

export const appTheme: ThemeOptions = {
    typography: {
        fontFamily: "'Karla', Helvetica, Arial, sans-serif",
        h4: {
            letterSpacing: '-0.03em',
            color: grey[800],
        },
        h5: {
            fontSize: '1.4rem',
            letterSpacing: '-0.03em',
            color: grey[600],
            lineHeight: '1.1em',
            marginBottom: 2 * spacing,
        },
        h6: {
            fontWeight: 'normal',
            fontSize: '1.1rem',
            lineHeight: '1.2em',
            marginBottom: spacing,
        },
        subtitle1: {
            fontWeight: 'bold',
        },
        subtitle2: {
            fontWeight: 'bold',
        },
        body1: {
            lineHeight: '1.4em',
            fontSize: `0.875rem`,
        },
        body2: {},
        caption: {
            fontSize: '0.85rem',
            color: grey[600],
            lineHeight: '1.2em',
        },
        overline: {
            fontSize: '0.8125rem',
            fontWeight: 'bold',
            letterSpacing: '0.03em',
            color: grey[500],
            lineHeight: '1.2em',
            textTransform: 'uppercase',
            marginBottom: '0.75em',
        },
    },
    breakpoint: THEME_BREAKPOINT,
    palette: {
        primary,
        secondary,
        grey,
        warning,
        error,
    },
    spacing,
    components: {
        MuiCssBaseline: {
            styleOverrides: fontsString,
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    background: grey[100],
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    paddingRight: 3 * spacing,
                    fontSize: '0.875rem',
                    lineHeight: '1.5em',
                },
                icon: {
                    color: grey[800],
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: '1em',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 'bold',
                    color: 'inherit',
                    letterSpacing: '-0.02em',
                    borderRadius: 1.5 * defaultBorderRadius,
                    padding: `${1 * spacing}px ${2 * spacing}px`,
                },
                contained: {
                    boxShadow: 'none',
                },
                text: {
                    padding: `${1 * spacing}px ${2 * spacing}px`,
                },
                outlined: {
                    borderWidth: `${defaultButtonBorderWidth}px !important`,
                    padding: `${1 * spacing - defaultButtonBorderWidth / 2}px ${
                        2 * spacing - defaultButtonBorderWidth / 2
                    }px !important`,
                },
                containedPrimary: {
                    color: 'white',
                },
                containedSecondary: {
                    color: 'white',
                },
                outlinedPrimary: {
                    color: primary[500],
                    borderColor: primary[500],
                },
                textPrimary: {
                    color: grey[800],
                },
                sizeSmall: {
                    borderRadius: 1.25 * defaultBorderRadius,
                    padding: `${0.75 * spacing}px ${1.5 * spacing}px`,
                },
                outlinedSizeSmall: {
                    padding: `${0.75 * spacing - defaultButtonBorderWidth / 2}px ${
                        1.5 * spacing - defaultButtonBorderWidth / 2
                    }px !important`,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: grey[800],
                    padding: spacing,

                    '&:hover': {
                        color: grey[900],
                    },
                },

                sizeSmall: {
                    padding: 3,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 3,
                    backgroundColor: primary[500],
                },
                flexContainer: {
                    height: '100%',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontWeight: 'bold',
                    color: grey[700],
                    paddingLeft: `${1.5 * spacing}px`,
                    paddingRight: `${1.5 * spacing}px`,

                    '&$selected': {
                        color: primary[600],
                    },
                    '@media (min-width: 600px)': {
                        minWidth: '20px !important',
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    fontWeight: 'bold',
                    color: grey[800],
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: '0.875rem',
                },
                underline: {
                    '&:before': {
                        borderBottomColor: 'rgba(0,0,0,0.15)',
                    },
                },
                multiline: {
                    paddingTop: spacing,
                    paddingBottom: spacing + 1,
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                gutters: {
                    padding: `0 ${2 * spacing}px !important`,
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                thumb: {
                    backgroundColor: grey[800],
                    marginTop: 0,
                    marginLeft: 0,
                },
                rail: {
                    backgroundColor: grey.main,
                },
                track: {
                    backgroundColor: grey[800],
                },
                markLabel: {
                    top: 'auto',
                    bottom: 14,
                    color: grey[500],
                    fontSize: '1.25em',
                },
                valueLabel: {
                    lineHeight: 1.2,
                    fontSize: 12,
                    background: 'unset',
                    padding: 0,
                    width: 32,
                    height: 32,
                    borderRadius: '50% 50% 50% 0',
                    backgroundColor: primary.main,
                    transformOrigin: 'bottom left',
                    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
                    '&::before': { display: 'none' },
                    '&.MuiSlider-valueLabelOpen': {
                        transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
                    },
                    '& > *': {
                        transform: 'rotate(45deg)',
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: '1.1rem',
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    display: 'flex',
                },
                marginNormal: {
                    marginTop: spacing,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    backgroundColor: grey[300],
                },
                vertical: {
                    marginTop: 1 * spacing,
                    marginBottom: 1 * spacing,
                    height: 24,
                },
                middle: {
                    marginLeft: 2 * spacing,
                    marginRight: 2 * spacing,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                tag: {
                    margin: 0,
                    marginRight: 0.5 * spacing,
                    marginBottom: 0.75 * spacing,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.9rem',
                },
                head: {
                    paddingTop: 1.5 * spacing,
                    paddingBottom: 1.5 * spacing,
                    textTransform: 'uppercase',
                    fontSize: '1em',
                    letterSpacing: '0.03em',
                    color: grey[600],
                },
                stickyHeader: {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    borderRadius: 1 * spacing,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    maxHeight: '100%',
                    borderRadius: defaultBorderRadius,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                elevation1: {
                    boxShadow: `0px 1px 3px 0px rgba(0, 0, 0, ${0.2 * shadowFactor}), 0px 1px 1px 0px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }), 0px 2px 1px -1px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation2: {
                    boxShadow: `0px 1px 5px 0px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 2px 2px 0px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 3px 1px -2px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation3: {
                    boxShadow: `0px 1px 8px 0px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 3px 4px 0px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 3px 3px -2px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation4: {
                    boxShadow: `0px 2px 4px -1px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 4px 5px 0px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 1px 10px 0px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation5: {
                    boxShadow: `0px 3px 5px -1px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 5px 8px 0px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 1px 14px 0px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation6: {
                    boxShadow: `0px 3px 5px -1px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 6px 10px 0px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 1px 18px 0px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation7: {
                    boxShadow: `0px 4px 5px -2px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 7px 10px 1px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 2px 16px 1px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation8: {
                    boxShadow: `0px 5px 5px -3px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 8px 10px 1px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 3px 14px 2px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation9: {
                    boxShadow: `0px 5px 6px -3px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 9px 12px 1px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 3px 16px 2px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation10: {
                    boxShadow: `0px 6px 6px -3px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 10px 14px 1px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 4px 18px 3px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation11: {
                    boxShadow: `0px 6px 7px -4px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 11px 15px 1px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 4px 20px 3px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation12: {
                    boxShadow: `0px 7px 8px -4px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 12px 17px 2px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 5px 22px 4px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation13: {
                    boxShadow: `0px 7px 8px -4px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 13px 19px 2px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 5px 24px 4px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation14: {
                    boxShadow: `0px 7px 9px -4px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 14px 21px 2px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 5px 26px 4px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation15: {
                    boxShadow: `0px 8px 9px -5px rgba(0, 0, 0, ${0.2 * shadowFactor}),0px 15px 22px 2px rgba(0, 0, 0, ${
                        0.14 * shadowFactor
                    }),0px 6px 28px 5px rgba(0, 0, 0, ${0.12 * shadowFactor})`,
                },
                elevation16: {
                    boxShadow: `0px 8px 10px -5px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 16px 24px 2px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 6px 30px 5px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation17: {
                    boxShadow: `0px 8px 11px -5px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 17px 26px 2px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 6px 32px 5px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation18: {
                    boxShadow: `0px 9px 11px -5px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 18px 28px 2px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 7px 34px 6px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation19: {
                    boxShadow: `0px 9px 12px -6px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 19px 29px 2px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 7px 36px 6px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation20: {
                    boxShadow: `0px 10px 13px -6px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 20px 31px 3px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 8px 38px 7px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation21: {
                    boxShadow: `0px 10px 13px -6px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 21px 33px 3px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 8px 40px 7px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation22: {
                    boxShadow: `0px 10px 14px -6px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 22px 35px 3px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 8px 42px 7px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation23: {
                    boxShadow: `0px 11px 14px -7px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 23px 36px 3px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 9px 44px 8px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
                elevation24: {
                    boxShadow: `0px 11px 15px -7px rgba(0, 0, 0, ${
                        0.2 * shadowFactor
                    }),0px 24px 38px 3px rgba(0, 0, 0, ${0.14 * shadowFactor}),0px 9px 46px 8px rgba(0, 0, 0, ${
                        0.12 * shadowFactor
                    })`,
                },
            },
        },
    },

    custom: {
        controlSelect: {
            '&:before': {
                border: 'none !important',
            },
        },
        defaultBorderRadius,
    },
};
